import { useTreasury } from "@/hooks/use-queries";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  ExternalLink,
  Fuel,
  DollarSign,
  Lock,
  BadgeDollarSign,
  Landmark,
  Wallet,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

function StatCard({
  icon: Icon,
  iconColor,
  label,
  value,
  sub,
  warn,
  loading,
}: {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  value: string;
  sub?: string;
  warn?: boolean;
  loading?: boolean;
}) {
  return (
    <Card className={`glass-panel transition-colors ${warn ? "border-amber-500/40 bg-amber-500/5" : ""}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg bg-surface-subtle ${iconColor}`}>
            <Icon className="h-4 w-4" />
          </div>
          {warn && (
            <Badge variant="outline" className="text-[9px] border-amber-500/50 text-amber-400 font-mono uppercase">
              Low
            </Badge>
          )}
        </div>
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
        {loading ? (
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-28 bg-surface-subtle" />
            <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <p className={`text-2xl font-display font-bold ${warn ? "text-amber-400" : ""}`}>{value}</p>
        )}
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function Treasury() {
  const { data, isLoading, isFetching, error, dataUpdatedAt } = useTreasury();
  const queryClient = useQueryClient();

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-treasury"] });

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-mono text-primary uppercase tracking-[0.2em] mb-1">Admin</h2>
          <h1 className="text-3xl font-display font-bold">Treasury Monitor</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Live treasury wallet and escrow contract balances. Auto-refreshes every 30s.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-[10px] font-mono text-muted-foreground">
              Updated {lastUpdated}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4 flex items-center gap-3 text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <p className="text-sm">Failed to load treasury stats — check API and RPC config.</p>
          </CardContent>
        </Card>
      )}

      {/* Treasury Wallet Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-bold uppercase tracking-wider font-mono">Treasury Wallet</h2>
          {data?.treasury?.address && (
            <a
              href={data.chain.explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] font-mono text-primary/60 hover:text-primary ml-2"
            >
              {data.treasury.address.slice(0, 10)}...{data.treasury.address.slice(-6)}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard
            icon={Fuel}
            iconColor="text-amber-400"
            label="Gas (ETH)"
            value={data ? `${parseFloat(data.treasury.ethBalance).toFixed(6)} ETH` : "—"}
            sub="Used for signing escrow transactions"
            warn={data?.treasury.ethLow}
            loading={isLoading && !data}
          />
          <StatCard
            icon={DollarSign}
            iconColor="text-blue-400"
            label="Treasury USDC"
            value={data ? `$${parseFloat(data.treasury.usdcBalance).toFixed(2)}` : "—"}
            sub="Treasury wallet liquid USDC"
            loading={isLoading && !data}
          />
        </div>

        {data?.treasury.ethLow && (
          <Card className="border-amber-500/40 bg-amber-500/5">
            <CardContent className="p-3 flex items-center gap-3">
              <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
              <div className="text-sm">
                <span className="font-bold text-amber-400">Low ETH Warning —</span>{" "}
                <span className="text-muted-foreground">
                  Treasury balance is below 0.01 ETH. Fund{" "}
                  <a
                    href={data.chain.explorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline font-mono text-[11px]"
                  >
                    {data.treasury.address.slice(0, 8)}…
                  </a>{" "}
                  on {data.chain.name} immediately to keep escrow operations running.
                </span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Escrow Contract Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-bold uppercase tracking-wider font-mono">Escrow Contract</h2>
          {data?.escrow?.address && (
            <a
              href={`${data.chain.explorerUrl.replace(/\/address\/.*/, '')}/address/${data.escrow.address}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] font-mono text-primary/60 hover:text-primary ml-2"
            >
              {data.escrow.address.slice(0, 10)}...{data.escrow.address.slice(-6)}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={Lock}
            iconColor="text-emerald-400"
            label="Locked in Escrow"
            value={data ? `$${parseFloat(data.escrow.lockedUsdc).toFixed(2)}` : "—"}
            sub="Protected balance, including refund fee reserve"
            loading={isLoading && !data}
          />
          <StatCard
            icon={BadgeDollarSign}
            iconColor="text-purple-400"
            label="Recorded Platform Fees"
            value={data ? `$${parseFloat(data.escrow.platformFeesUsdc).toFixed(2)}` : "—"}
            sub={data ? `${data.escrow.platformFeeBps / 100}% rate · withdrawal disabled on V2` : undefined}
            loading={isLoading && !data}
          />
          <StatCard
            icon={DollarSign}
            iconColor="text-blue-400"
            label="Contract Total USDC"
            value={data ? `$${parseFloat(data.escrow.totalUsdc).toFixed(2)}` : "—"}
            sub="All funds retained in V2"
            loading={isLoading && !data}
          />
        </div>
      </div>

      {/* Chain Info */}
      {data && (
        <Card className="glass-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground">
              Network
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Chain</p>
              <p className="font-bold font-mono">{data.chain.name}</p>
            </div>
            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Chain ID</p>
              <p className="font-bold font-mono">{data.chain.id}</p>
            </div>
            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">RPC</p>
              <p className="font-mono text-xs text-muted-foreground truncate">{data.chain.rpcUrl}</p>
            </div>
            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Status</p>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                <span className="font-mono text-emerald-400 text-xs">Connected</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
